import axios from "axios";
import { API_BASE_URL } from "@/config";

/* ---------- Konstanten ---------- */
const ACCESS_KEY = "access";
const REFRESH_KEY = "refresh";

/* ---------- Helper ---------- */
const getAccess = () => localStorage.getItem(ACCESS_KEY);
const getRefresh = () => localStorage.getItem(REFRESH_KEY);
const setTokens = ({ access, refresh }) => {
  if (access) localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
};
const clearTokens = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

/* ---------- Instanzen ---------- */
const contactApi = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 10000,
  headers: { "Content-Type": "application/json" },
});

const authApi = axios.create({
  baseURL: `${API_BASE_URL}/auth`,
  timeout: 10000,
  headers: { "Content-Type": "application/json" },
  withCredentials: false, // JWT im Header → ok
});

/* ---------- Request: immer Bearer anhängen ---------- */
contactApi.interceptors.request.use((cfg) => {
  const token = getAccess();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  else delete cfg.headers.Authorization;
  return cfg;
});

/* ---------- Response: 401 → Refresh → Retry ---------- */
let refreshPromise = null;

contactApi.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (status === 401 && !original?._retry) {
      original._retry = true;

      // nur einen Refresh gleichzeitig ausführen
      if (!refreshPromise) {
        refreshPromise = (async () => {
          const refresh = getRefresh();
          if (!refresh) throw error;
          const { data } = await authApi.post("/jwt/refresh/", { refresh });
          // Djoser liefert nur access; eigenen refresh behalten
          setTokens({ access: data.access, refresh: data.refresh || refresh });
          return data.access;
        })().finally(() => {
          refreshPromise = null;
        });
      }

      try {
        const newAccess = await refreshPromise;
        // Retry mit neuem Token
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return contactApi(original);
      } catch (e) {
        // Refresh fehlgeschlagen → sauber ausloggen
        clearTokens();
        // optional: location.href = '/login';
        throw e;
      }
    }

    throw error;
  }
);

/* ---------- Auth ---------- */
export const login = async ({ username, password }) => {
  const { data } = await authApi.post("/jwt/create/", { username, password });
  setTokens({ access: data.access, refresh: data.refresh });
  return data;
};

export const refreshToken = async () => {
  const refresh = getRefresh();
  const { data } = await authApi.post("/jwt/refresh/", { refresh });
  setTokens({ access: data.access, refresh: data.refresh || refresh });
  return data;
};

export const logout = async () => {
  // optional: serverseitiges Logout/Blacklist, falls vorhanden:
  // try { await authApi.post('/jwt/logout/', { refresh: getRefresh() }); } catch {}
  clearTokens();
};

/* ---------- Portfolio ---------- */
export const fetchProjects = async () => {
  const { data } = await contactApi.get("/portfolio/projects/");
  return Array.isArray(data) ? data : (data?.projects ?? []);
};

export const fetchProject = async (id) => {
  const { data } = await contactApi.get(`/portfolio/projects/${id}/`);
  return data;
};

/* ---------- Kontakt ---------- */
export const submitContactForm = async (formData) => {
  const { data } = await contactApi.post("/contact/", {
    name: formData.name,
    company: formData.company,
    email: formData.email,
    phone: formData.phone || "",
    service: formData.service || "",
    budget: formData.budget || "",
    message: formData.message,
    privacy_accepted: formData.privacy,
  });
  return data;
};

// --- Auth-Status prüfen (Access verifizieren, ggf. refreshen) ---
export const verifyAuth = async () => {
  const access = getAccess();
  if (!access) return false;

  try {
    // Djoser: /auth/jwt/verify/ erwartet { token }
    await authApi.post("/jwt/verify/", { token: access });
    return true; // Access ist gültig
  } catch (_) {
    // Access ungültig → versuchen zu refreshen
    try {
      await refreshToken();           // nutzt bereits setTokens()
      return true;                    // nach Refresh ok
    } catch {
      clearTokens();                  // komplett abmelden
      return false;
    }
  }
};

export default contactApi;
